import React, {
  useMemo,
  useCallback,
  useState,
} from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSelector, useDispatch } from 'react-redux';
import { List } from 'immutable';
import { selectAllQuestions } from 'models/questions';
import { updateFilter, resetFilter, defaultFilters } from 'models/participantFilter';

import ParticipantFilter from './ParticipantFilter';
import ParticipantMobileFilter from './ParticipantMobileFilter';

const ParticipantFilterSwitch = () => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const questions = useSelector(selectAllQuestions);
  const dispatch = useDispatch();
  const participantFilter = useSelector((state: any) => state.participantFilter);
  const [mobileParticipantFilter, setMobileParticipantFilter] = useState(participantFilter);
  const filters = useMemo(() => participantFilter?.get('filters'), [participantFilter]);
  const queryFilters = useMemo(() => participantFilter?.get('queryFilters'), [participantFilter]);
  const [openedQuestions, setOpenedQuestions] = useState(participantFilter?.get('openedQuestionIds'));
  const {
    initialLoading,
    loading,
  } = useSelector((state: any) => state.participants);
  const isLoading = useMemo(() => initialLoading || loading, [initialLoading, loading]);

  const resetSelectedChoices = useCallback(() => {
    if (isLoading) return;
    if (isDesktop) {
      dispatch(resetFilter());
    } else {
      setMobileParticipantFilter(defaultFilters);
    }
  }, [dispatch, isLoading, participantFilter, mobileParticipantFilter]);

  const onChoiceSelect = useCallback((questionId: string, choiceId: string, hasSubQuestions: boolean, queryField: string) => {
    if (isLoading) return;
    let newParticipantFilter = isDesktop ? participantFilter : mobileParticipantFilter;
    let choices = isDesktop ? filters.get(questionId) : mobileParticipantFilter?.get('filters').get(questionId);
    choices = choices || List();
    const index = choices.findIndex((choice) => choice === choiceId);
    if (index !== -1) {
      choices = choices.delete(index);
    } else {
      choices = choices.push(choiceId);
    }
    newParticipantFilter = newParticipantFilter.setIn(['filters', questionId], choices);
    let queryChoices = isDesktop ? queryFilters.get(queryField) : mobileParticipantFilter?.get('queryFilters').get(queryField);
    queryChoices = queryChoices || List();
    const queryIndex = queryChoices.findIndex((choice) => choice === choiceId);
    if (queryIndex !== -1) {
      queryChoices = queryChoices.delete(queryIndex);
    } else {
      queryChoices = queryChoices.push(choiceId);
    }
    if (hasSubQuestions && index !== -1) {
      const question = questions.find((it) => it.id === questionId);
      const choice = question.choices.find((it) => it.id === choiceId);
      choice.subQuestions.forEach((subQuestion) => {
        newParticipantFilter = newParticipantFilter.setIn(['filters', subQuestion.id], List());
        const questionIds = subQuestion.choices.map(({ id }) => id);
        queryChoices = queryChoices.filter((it: string) => !questionIds.includes(it));
      });
    }
    newParticipantFilter = newParticipantFilter.setIn(['queryFilters', queryField], queryChoices);
    if (isDesktop) {
      dispatch(updateFilter({
        field: [],
        value: newParticipantFilter,
      }));
    } else {
      setMobileParticipantFilter(newParticipantFilter);
    }
  }, [dispatch, filters, isDesktop, isLoading, mobileParticipantFilter, participantFilter, queryFilters, questions]);

  const handleQuestionOpen = useCallback((questionId) => {
    if (isLoading) return;
    const index = openedQuestions.findIndex((id) => questionId === id);
    let newOpenedQuestions = openedQuestions;
    if (index !== -1) {
      newOpenedQuestions = newOpenedQuestions.delete(index);
    } else {
      newOpenedQuestions = newOpenedQuestions.push(questionId);
    }
    setOpenedQuestions(newOpenedQuestions);
    dispatch(updateFilter({
      field: ['openedQuestionIds'],
      value: newOpenedQuestions,
    }));
  }, [dispatch, isLoading, openedQuestions]);

  const applyClickedOptions = () => {
    dispatch(updateFilter({
      field: [],
      value: mobileParticipantFilter,
    }));
  };

  return isDesktop ? (
    <ParticipantFilter
      questions={questions}
      onChoiceSelect={onChoiceSelect}
      resetSelectedChoices={resetSelectedChoices}
      handleQuestionOpen={handleQuestionOpen}
      isLoading={isLoading}
    />
  ) : (
    <ParticipantMobileFilter
      questions={questions}
      onChoiceSelect={onChoiceSelect}
      resetSelectedChoices={resetSelectedChoices}
      handleQuestionOpen={handleQuestionOpen}
      applyClickedOptions={applyClickedOptions}
      isLoading={isLoading}
      mobileParticipantFilter={mobileParticipantFilter}
    />
  );
};

export default ParticipantFilterSwitch;
